import { useMemo } from "react";
import "@style/TestSections.scss";
import { buildProctorProvidedFieldRows } from "@/utils/proctorProvidedFields";

export type ProctorFieldsBarMode = "request" | "inTest";

type ProctorProvidedFieldsBarProps = {
    testState?: Record<string, unknown> | null;
    valueOverrides?: Record<string, unknown> | null;
    /** `request` — proctor-provided fields only; `inTest` — any configured field that has a value. */
    mode?: ProctorFieldsBarMode;
};

export default function ProctorProvidedFieldsBar({
    testState,
    valueOverrides,
    mode = "request",
}: ProctorProvidedFieldsBarProps) {
    const rows = useMemo(
        () => buildProctorProvidedFieldRows(testState, valueOverrides, mode),
        [testState, valueOverrides, mode]
    );
    const hasRowValue = (value: unknown) => String(value ?? "").trim().length > 0;
    const isInTestMode = mode === "inTest";
    const hasAnyValue = rows.some((row) => hasRowValue(row.value));

    if (!rows.length || !hasAnyValue) {
        return null;
    }

    const ariaLabel = isInTestMode ? "Test information" : "Proctor provided information";

    return (
        <div className="proctorProvidedFieldsBar" role="region" aria-label={ariaLabel}>
            {rows.map((row) => {
                const hasValue = hasRowValue(row.value);
                if (isInTestMode && !hasValue) {
                    return null;
                }
                return (
                    <span
                        key={row.id}
                        className={`proctorProvidedField${hasValue ? "" : " pending"}`}
                    >
                        <span className="label">{row.label}:</span>
                        <span className="value">{hasValue ? row.value : "—"}</span>
                    </span>
                );
            })}
        </div>
    );
}
